import axios from "axios";
import { endOfDay, isBefore } from "date-fns";

import publicConfig from "config/public";
import { getCookie } from "utils/cookies";
import logError from "utils/logError";
import { availableStorage } from "utils/storage";

const ONE_HOUR = 3600000; // 60 * 60 * 1 * 1000 miliseconds;
const ONE_DAY = ONE_HOUR * 24;

const {
  services: { reservationsApi, consumerApi },
} = publicConfig;

const parseQuery = (url) => {
  try {
    const queryString = url.split("?")[1];
    if (!queryString) {
      return;
    }
    const query = {};
    const pairs = (
      queryString[0] === "?" ? queryString.substr(1) : queryString
    ).split("&");

    for (let i = 0; i < pairs.length; i++) {
      const pair = pairs[i].split("=");
      query[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1] || "");
    }
    return query;
  } catch {
    logError("Failed to process data in parseQuery", {
      functionName: "getOrders",
    });
    return {};
  }
};

export const capGuestOrders = (guestOrders) => {
  if (guestOrders.length <= 5) {
    return guestOrders;
  }
  // takes last 5 since guest orders are pushed onto the array at the end
  return guestOrders.slice(guestOrders.length - 5);
};

export const getGuestOrderIds = () => {
  try {
    const url = window.location.href;
    const params = parseQuery(url);
    const storage = availableStorage();

    const guestOrdersKey = "guestOrders";
    const guestOrders =
      JSON.parse(storage && storage.getItem(guestOrdersKey)) || [];

    const guestToken =
      url.includes("order-status/") && params && params.guestToken;
    const orderId = url.substring(
      url.lastIndexOf("/") + 1,
      url.lastIndexOf("?"),
    );

    if (
      guestToken &&
      orderId &&
      !guestOrders.find((guestOrder) => guestOrder.orderId === orderId)
    ) {
      guestOrders.push({ guestToken, orderId });
      storage && storage.setItem(guestOrdersKey, JSON.stringify(guestOrders));
    }
    return capGuestOrders(guestOrders);
  } catch {
    logError(`Failed to process data in getGuestOrderIds`, {
      functionName: "getOrders",
    });
    return [];
  }
};
const getGuestOrders = async () => {
  const guestOrderIds = getGuestOrderIds();
  if (!getGuestOrderIds || guestOrderIds.length === 0) {
    return [];
  }
  const url = `${reservationsApi.url}/reservations`;
  const orders = await Promise.all(
    guestOrderIds.map(async ({ orderId, guestToken }) => {
      const orderUrl = `${url}/${orderId}?guest_token=${guestToken}`;
      try {
        const {
          data: { data },
        } = await axios.get(orderUrl, {
          // this header is necessary due to validation on res-api but
          // it will not ever actually be used, it just needs to be present.
          // but we will either always be a logged in user or a guest user that
          // has a guest order and we fetch it by id and guest_token.
          // It is an artifact of kiosk.
          headers: { "X-Leafly-Id": "chat" },
        });
        return data;
      } catch (e) {
        return undefined;
      }
    }),
  );

  const allOrders = orders.filter((order) => order);

  const historicalStatuses = ["canceled", "arrived_at_customer", "picked_up"];
  const historical = allOrders.filter((order) =>
    historicalStatuses.includes(order.status),
  );
  const active = allOrders.filter(
    (order) => !historicalStatuses.includes(order.status),
  );
  return filterOrdersByTime(active, historical);
};

export const withinScheduleWindow = (order) => {
  if (
    !order ||
    !order.fulfillmentMechanism === "delivery" ||
    !order.endOfScheduledWindow
  ) {
    return false;
  }

  return isBefore(
    new Date(),
    endOfDay(new Date(`${order.endOfScheduledWindow}`)),
  );
};

const filterOrdersByTime = (active, historical) => [
  // filter orders that are older than 1 day old.
  ...active.filter(
    (order) =>
      withinScheduleWindow(order) ||
      new Date(order.createdAt) >= new Date(+new Date() - ONE_DAY),
  ),
  ...historical.filter(
    (order) =>
      withinScheduleWindow(order) ||
      new Date(order.pickedUpAt || order.deliveredAt || order.canceledAt) >=
        new Date(+new Date() - ONE_HOUR),
  ),
];

export const applyDispensaryProperties = async (orders) =>
  (
    await Promise.all(
      orders.map(async (order) => {
        try {
          const updatedOrder = order;

          if (!updatedOrder.cart) {
            await axios
              .get(`${reservationsApi.url}/reservations/${updatedOrder.id}`, {
                credentials: "include",
                headers: { "X-Leafly-Id": "chat" },
                withCredentials: true,
              })
              .then(({ data }) => {
                updatedOrder.cart = data.data.cart;
              });
          }

          await axios
            .get(
              `${consumerApi.url}/api/dispensaries/v1/${updatedOrder.cart.dispensaryData.slug}`,
            )
            .then(({ data }) => {
              updatedOrder.dispensaryHasChatEnabled = data.hasChatEnabled;
              updatedOrder.dispensaryPhone = data.phone;
            });

          return updatedOrder;
        } catch (e) {
          logError(
            `Failed to apply dispensary properties to order id ${order.id}}`,
            { functionName: "getOrders" },
          );

          return undefined;
        }
      }),
    )
  ).filter(Boolean);

export const getOrders = async () => {
  const url = `${reservationsApi.url}/reservations`;
  const guestOrders = await getGuestOrders();

  const userId = getCookie("leafly.userid");
  const orders =
    userId && userId.length > 0
      ? await axios
          .get(url, {
            credentials: "include",

            // this header is necessary due to validation on res-api but
            // it will not ever actually be used, it just needs to be present.
            // but we will either always be a logged in user or a guest user that
            // has a guest order and we fetch it by id and guest_token.
            // It is an artifact of kiosk.
            headers: { "X-Leafly-Id": "chat" },

            params: { page: 1, per: 5 },
            withCredentials: true,
          })
          .then(({ data: { data } }) => {
            return data;
          })
          .then(({ active, historical }) => {
            const filteredOrders = filterOrdersByTime(active, historical);
            return filteredOrders;
          })
          .catch(() => [])
      : [];

  let allOrders = [...(guestOrders || []), ...(orders || [])].sort(
    (o1, o2) => +new Date(o1.createdAt) - +new Date(o2.createdAt),
  );
  // We unique the orders here because it is possible for a user to create an account after
  // guest checkout - This results in a scenario where there is a duplicate chat; one coming from authenticated
  // reservations and one coming from a local storage guest checkout key
  allOrders = [
    ...new Map(allOrders.map((order) => [order.id, order])).values(),
  ];
  const historicalStatuses = ["canceled", "arrived_at_customer", "picked_up"];

  const activeOrders = allOrders.filter(
    (order) => !historicalStatuses.includes(order.status),
  );

  const historicalOrders = allOrders.filter((order) =>
    historicalStatuses.includes(order.status),
  );

  // prioritize showing active versus historical and show historical when there is room
  const limitedOrders = [...activeOrders, ...historicalOrders].reverse();

  return await applyDispensaryProperties(limitedOrders);
};
